import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { NetworkTreeNode, ScrollContext } from './NetworkTreeNode';
import UserDetailModal from './UserDetailModal';
import TreeUserDetailModal from './TreeUserDetailModal';
import { IoSearchOutline, IoAddOutline, IoRemoveOutline, IoReloadOutline } from 'react-icons/io5';
import { Tooltip } from 'react-tooltip';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import './NetworkTree.css';

// Reusable logic from the original tree system to standardize object shape
const mapUser = (u) => {
  if (!u) return null;
  return {
    id: u._id || Math.random().toString(),
    name: u.username || 'Unknown User',
    referralCode: u.referral_code || 'N/A',
    wallet: u.walletAddress || '0x00...00',
    level: u.level || 0,
    joinDate: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A',
    childrenCount: u.children ? u.children.length : 0,
    autoExpand: !!u.autoExpand,
    isSearchedTarget: !!u.isSearchedTarget,
    children: u.children ? u.children.map(mapUser) : []
  }
};

export default function NetworkTree() {
  const navigate = useNavigate();
  const [activeTreeData, setActiveTreeData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null); // popup state
  
  const containerRef = useRef(null); // Container for ScrollContext
  const transformRef = useRef(null); // TransformWrapper API ref

  const handleRedirectToProfile = (wallet) => {
    setSelectedUser(null); // Close the current modal
    navigate(`/validator-delegator/referral-list?walletAddress=${wallet}`);
  };

  // Derive active backend domain from environment configuration
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  const BASE_URL = environment === 'production' 
    ? process.env.REACT_APP_PROD_API_URL 
    : environment === 'staging' 
      ? process.env.REACT_APP_STAG_API_URL 
      : process.env.REACT_APP_DEV_API_URL || 'http://localhost:4001';

  const fetchNetwork = async () => {
    setLoading(true);
    setActiveTreeData(null);
    try {
      const { data: resData } = await axios.get(`${BASE_URL}/v1/validator-delegator/network`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('acessToken')}`,
          Accept: 'application/json'
        }
      });
      const dataObj = resData?.data;
      if (dataObj) {
         setActiveTreeData(mapUser(dataObj));
      }
    } catch (err) {
      console.error("Failed to load network", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTreeByCode = async (code) => {
    if (!code) {
      fetchNetwork();
      return;
    }
    setLoading(true);
    setActiveTreeData(null);
    try {
      const { data: resData } = await axios.get(`${BASE_URL}/v1/validator-delegator/tree?code=${code}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('acessToken')}`,
        }
      });
      const dataObj = resData?.data;
      if (dataObj) {
        const treeRoot = Array.isArray(dataObj) ? dataObj[0] : dataObj;
        setActiveTreeData(mapUser(treeRoot));
      } else {
        setActiveTreeData(null);
      }
    } catch (err) {
       console.error("Failed to load generic referral search", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNetwork();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (transformRef.current) transformRef.current.resetTransform();
    if (searchTerm.trim()) {
      fetchTreeByCode(searchTerm.trim());
    } else {
      fetchNetwork();
    }
  };

  // --- Native Wheel Panning Bridge (Fixes Windows Trackpad issues) ---
  useEffect(() => {
    // We use a native listener with { passive: false } to ensure we can e.preventDefault()
    // which is required to stop the browser from zooming/scrolling while we pan.
    const container = containerRef.current;
    if (!container) return;

    const handleNativeWheel = (e) => {
      // If Control/Cmd is held, it's a zoom attempt (Pinch or Ctrl+Scroll).
      // We let the library handle this via its configured 'wheel' settings.
      if (e.ctrlKey) return;

      // Otherwise, hijack the event for Panning (2-finger move)
      if (transformRef.current) {
        e.preventDefault();
        const { state, setTransform } = transformRef.current;
        
        // Multiplier for trackpad sensitivity (1.0 is default, can adjust if too fast/slow)
        const moveX = e.deltaX;
        const moveY = e.deltaY;

        setTransform(
          state.positionX - moveX,
          state.positionY - moveY,
          state.scale,
          0,
          "linear"
        );
      }
    };

    container.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleNativeWheel);
  }, [activeTreeData, loading]);

  useEffect(() => {
    // Auto-center or reset when data loads
    if (activeTreeData && transformRef.current) {
       setTimeout(() => {
         transformRef.current.centerView(1.0);
       }, 100);
    }
  }, [activeTreeData]);

  return (
    <div className="referral_container p-0 position-relative" style={{ height: '100%', overflow: 'hidden' }}>
      
      {/* Global Floating Search Header - Moved outside to ensure typing works and it stays fixed */}
      <div className="network-tree-search-bar">
        <form onSubmit={handleSearchSubmit} className="network-tree-search-form">
          <IoSearchOutline className="network-tree-search-icon" size={20} />
          <input 
            type="text" 
            placeholder="Search Username, Code or Wallet..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="network-tree-search-input"
          />
        </form>
      </div>

      {/* Zoom Controls (Fixed UI) */}
      <div className="position-absolute bottom-0 end-0 m-4 d-flex flex-column gap-2" style={{ zIndex: 100 }}>
        <button 
          onClick={() => transformRef.current?.zoomIn()} 
          className="btn btn-light shadow-sm d-flex align-items-center justify-content-center rounded-circle" 
          style={{ width: '45px', height: '45px', border: '1px solid #dee2e6' }}
        >
          <IoAddOutline size={22} color="#234b9a" />
        </button>
        <button 
          onClick={() => transformRef.current?.resetTransform()} 
          className="btn btn-light shadow-sm d-flex align-items-center justify-content-center rounded-circle" 
          style={{ width: '45px', height: '45px', border: '1px solid #dee2e6' }}
          title="Reset View"
        >
          <IoReloadOutline size={20} color="#234b9a" />
        </button>
        <button 
          onClick={() => transformRef.current?.zoomOut()} 
          className="btn btn-light shadow-sm d-flex align-items-center justify-content-center rounded-circle" 
          style={{ width: '45px', height: '45px', border: '1px solid #dee2e6' }}
        >
          <IoRemoveOutline size={22} color="#234b9a" />
        </button>
      </div>

      <ScrollContext.Provider value={containerRef}>
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={0.3}
          maxScale={2}
          centerOnInit={true}
          limitToBounds={false}
          alignmentAnimation={{ disabled: true }}
          doubleClick={{ disabled: true }}
          wheel={{
            activationKeys: ["Control"], // Only zoom if Control is held
          }}
          panning={{
            disabled: false,
            wheelPanning: false, // Explicitly disabled to let our manual native bridge handle it
          }}
        >
          <TransformComponent
            wrapperClass="network-tree-canvas-container custom-scrollbar"
            contentClass="network-tree-infinite-plane-wrapper"
          >
            <div ref={containerRef} className="network-tree-scroll-anchor">
               
               <div className="network-tree-infinite-plane">
                 {loading ? (
                   <div className="d-flex flex-column align-items-center mt-5">
                     <div className="spinner-border text-primary" role="status">
                       <span className="visually-hidden">Loading...</span>
                     </div>
                     <span className="fw-bold text-muted mt-3">Synchronizing B-Tree Network...</span>
                   </div>
                 ) : activeTreeData ? (
                   <NetworkTreeNode user={activeTreeData} isRoot={true} onShowDetails={setSelectedUser} />
                 ) : (
                   <div className="d-flex flex-column align-items-center mt-5">
                     <span className="fw-bold text-danger">No network found for this criteria.</span>
                   </div>
                 )}
               </div>
            </div>
          </TransformComponent>
        </TransformWrapper>
      </ScrollContext.Provider>

      {/* Segregated User Detail Modal with Live On-Chain Hooks */}
      <TreeUserDetailModal 
        walletAddress={selectedUser?.wallet} 
        onClose={() => setSelectedUser(null)} 
        onViewFullProfile={handleRedirectToProfile}
      />

      {/* Global Tooltip Anchor for High-Density Card Data */}
      <Tooltip id="tree-tooltip" style={{ zIndex: 9999, backgroundColor: '#111827', borderRadius: '8px', fontSize: '12px' }} />

    </div>
  );
}
